import React, { useState } from 'react';
import { 
  Smartphone, Monitor, Apple, Download, CheckCircle2, 
  Sparkles, ShieldCheck, Zap, Layers,
  Brain, ExternalLink, Plus, Copy, X, Check, Radio, Share2
} from 'lucide-react';
import { DISABILITY_CLUSTERS, DisabilityCategory } from '../data/disabilityTaxonomy';

interface DownloadAppsPageProps {
  onLaunchModule?: (moduleId: string) => void;
}

export const DownloadAppsPage: React.FC<DownloadAppsPageProps> = ({ onLaunchModule }) => {
  const [downloadSuccess, setDownloadSuccess] = useState('');
  const [selectedDisabilityId, setSelectedDisabilityId] = useState<string>(DISABILITY_CLUSTERS[0].id);
  const [platformFilter, setPlatformFilter] = useState<'all' | 'android' | 'windows' | 'macos'>('all');
  const [viewMode, setViewMode] = useState<'selected' | 'all-disabilities'>('selected');
  const [shortcutModalModule, setShortcutModalModule] = useState<{ id: string; name: string; desc: string } | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const fullApps = [
    {
      title: 'AbilityOS Full Suite Enterprise Android App (.APK)',
      platform: 'Android 8.0+ (ARM64 & x86_64, Tablets, Chromebooks)',
      version: 'v1.0.5 Enterprise Build',
      size: '41.1 MB',
      filename: 'AbilityOS_Android_Enterprise_v1.0.apk',
      icon: Smartphone,
      highlight: 'Recommended for Tablets'
    },
    {
      title: 'AbilityOS Full Suite Windows Desktop App (.EXE)',
      platform: 'Windows 10 / 11 (64-bit / ARM64, Therapy Laptops)',
      version: 'v1.0.5 Setup Installer',
      size: '41.1 MB',
      filename: 'AbilityOS_Windows_Enterprise_v1.0_Setup.exe',
      icon: Monitor,
      highlight: 'For Clinics & Schools'
    },
    {
      title: 'AbilityOS Full Suite macOS App (.DMG)',
      platform: 'macOS 12.0+ (Universal: M1/M2/M3/M4 & Intel)',
      version: 'v1.0.5 Universal Disk Image',
      size: '41.1 MB',
      filename: 'AbilityOS_macOS_Enterprise_v1.0.dmg',
      icon: Apple,
      highlight: 'Apple Silicon Certified'
    },
    {
      title: 'AbilityOS Air-Gapped Offline Suite (.ZIP)',
      platform: 'Cross-Platform (Windows, Mac, Linux, Zero-Internet Schools)',
      version: 'v1.0.5 Offline Edition',
      size: '41.0 MB',
      filename: 'AbilityOS_Complete_Offline_Suite_v1.0.zip',
      icon: Layers,
      highlight: 'Air-Gapped Intranet'
    }
  ];

  const moduleAppsCatalog: Record<string, { id: string; name: string; filePrefix: string; desc: string; iconName?: string }> = {
    'aac': { id: 'aac', name: 'AAC Speech Synthesizer', filePrefix: 'AAC_SpeechSynthesizer', desc: 'Augmentative speech board & natural TTS voice engine' },
    'speech-therapy': { id: 'speech-therapy', name: 'Speech Therapy & Articulation', filePrefix: 'SpeechTherapy_Articulation', desc: 'Phonetic drills & vocal spectrogram waveform visualizer' },
    'audiometer': { id: 'audiometer', name: 'Diagnostic Pure-Tone Audiometer', filePrefix: 'Audiometer_Hearing_Screener', desc: 'Binaural 125-8000Hz pure-tone audiogram with Speech Banana & PTA severity overlay' },
    'autism': { id: 'autism', name: 'Autism & ABA Behavior Manager', filePrefix: 'Autism_ABA_Behavior', desc: 'ABC incident logger & sensory meltdown trigger tracker' },
    'sign-language': { id: 'sign-language', name: 'Sign Language & Gestures', filePrefix: 'SignLanguage_Gestures', desc: 'Indian Sign Language (ISL) video dictionary & gesture trainer' },
    'braille': { id: 'braille', name: 'Braille & Tactile Learning', filePrefix: 'Braille_Tactile_Learning', desc: '6-dot Nemeth Braille translator & screen reader' },
    'student': { id: 'student', name: 'Student IEP Progress Desk', filePrefix: 'Student_IEP_Progress', desc: 'SMART IEP goal builder & milestone progress tracker' },
    'therapist': { id: 'therapist', name: 'Therapist Workspace & SOAP', filePrefix: 'Therapist_SOAP_Notes', desc: 'Clinical SOAP note editor & longitudinal treatment case history' },
    'teacher': { id: 'teacher', name: 'Teacher Classroom & Lesson Studio', filePrefix: 'Teacher_Classroom_Lesson', desc: 'Differentiated curriculum and special lesson plan builder' },
    'assessment': { id: 'assessment', name: 'Diagnostic Clinical Assessment', filePrefix: 'Diagnostic_Clinical_Assessment', desc: 'Sensory Profile 2 & CARS-2 standardized evaluators' },
    'school-erp': { id: 'school-erp', name: 'Special School ERP & Timetable', filePrefix: 'SpecialSchool_ERP_Timetable', desc: 'Wheelchair bus GPS route tracker & daily student rosters' },
    'hospital': { id: 'hospital', name: 'Rehabilitation Hospital Rounds', filePrefix: 'RehabHospital_PatientCare', desc: 'Inpatient ward bed manager & multidisciplinary rounds' },
    'billing': { id: 'billing', name: 'NDIS & Clinical Insurance Billing', filePrefix: 'NDIS_Clinical_Billing', desc: 'Automated claim generation with NDIS and insurance code maps' },
    'communication': { id: 'communication', name: 'Parent Communication Hub', filePrefix: 'ParentComm_Messenger', desc: 'Encrypted caregiver messaging & daily milestone photos' },
    'inventory': { id: 'inventory', name: 'Assistive Device Inventory', filePrefix: 'AssistiveDevice_Inventory', desc: 'Wheelchair, hearing aid, and AAC tablet hardware lender' },
    'analytics': { id: 'analytics', name: 'Executive Analytics BI', filePrefix: 'ExecutiveAnalytics_BI', desc: 'Institutional compliance gauges & clinical audit charts' },
    'ai': { id: 'ai', name: 'AI Copilot & Clinical Summarizer', filePrefix: 'AICopilot_ReportSummarizer', desc: 'Automated SMART goal prompts & therapist note summarizer' },
    'sensory-room': { id: 'sensory-room', name: 'Sensory Room Snoezelen Controller', filePrefix: 'SensoryRoom_Snoezelen_Controller', desc: 'Interactive calm lighting presets & ambient soundscapes' },
    'tele-rehab': { id: 'tele-rehab', name: 'Tele-Rehabilitation Video Consult', filePrefix: 'TeleRehab_RemoteVideo_Therapy', desc: 'Encrypted HD video consultations with interactive switch board' },
    'early-intervention': { id: 'early-intervention', name: 'Early Intervention Milestone Tracker', filePrefix: 'EarlyIntervention_Developmental_Tracker', desc: 'Pediatric developmental checklists for infants & toddlers (0-36 Mos)' },
    'vocational-living': { id: 'vocational-living', name: 'Vocational & Life Skills Studio', filePrefix: 'Vocational_IndependentLiving_Skills', desc: 'Adult disability work readiness, transit training & ADL tasks' },
    'hydrotherapy': { id: 'hydrotherapy', name: 'Hydrotherapy & Aquatic Physical Rehab', filePrefix: 'Hydrotherapy_AquaticRehab_Controller', desc: 'Pool temperature & aquatic buoyancy exercises for spasticity' },
    'music-therapy': { id: 'music-therapy', name: 'Music Therapy & Auditory Entrainment', filePrefix: 'MusicTherapy_RhythmicAuditory_Stimulation', desc: 'Rhythmic Auditory Stimulation (RAS) metronome for gait timing' },
    'animal-therapy': { id: 'animal-therapy', name: 'Animal-Assisted Therapy & Pet Companion', filePrefix: 'AnimalAssisted_Therapy_PetCare', desc: 'Therapy dog interaction logger & emotional calming telemetry' },
    'prosthetics': { id: 'prosthetics', name: 'Bionic Prosthetics & Exoskeleton Fitting', filePrefix: 'Prosthetics_Orthotics_Exoskeleton_Fitting', desc: 'Myoelectric sensor calibration, bionic hand telemetry & gait tracking' },
    'vbmapp-assessment': { id: 'vbmapp-assessment', name: 'VB-MAPP & ABLLS-R Behavioral Battery', filePrefix: 'VBMAPP_Behavioral_Assessment_Protocol', desc: '170-milestone developmental assessment & barrier grid' },
    'core-vocab-ai': { id: 'core-vocab-ai', name: 'Motor Core Vocab & AI Word Predictor', filePrefix: 'CoreVocab_MotorPlanning_AI_Predictor', desc: 'Fixed Crescendo motor-planning grid with neural prediction' },
    'fim-weefim': { id: 'fim-weefim', name: 'FIM & WeeFIM Functional Independence', filePrefix: 'FIM_WeeFIM_Clinical_Scoring', desc: '18-item 7-level functional independence scorecard' },
    'evv-gps-tracker': { id: 'evv-gps-tracker', name: 'EVV Visit Verification & GPS', filePrefix: 'EVV_GPS_Caregiver_CheckIn', desc: 'Federal 21st Century Cures Act compliant caregiver check-in' },
    'gamified-neuro': { id: 'gamified-neuro', name: 'WebGL Biofeedback Neuro-Rehab Games', filePrefix: 'Gamified_WebGL_Neuro_Rehab', desc: 'Interactive gamified reaction target tracking for motor recovery' },
    'transition-matrix': { id: 'transition-matrix', name: 'Secondary Transition & Career Matrix', filePrefix: 'Secondary_Transition_Vocational_Matrix', desc: 'IDEA Sec 300.320 career assessment cards & supported employment' }
  };

  const currentCluster: DisabilityCategory = DISABILITY_CLUSTERS.find(c => c.id === selectedDisabilityId) || DISABILITY_CLUSTERS[0];
  const ClusterIcon = currentCluster.icon;

  // Retrieve prescribed apps for selected disability cluster
  const primaryApps = currentCluster.primaryModuleIds
    .map(id => moduleAppsCatalog[id])
    .filter(Boolean);

  const secondaryApps = currentCluster.secondaryModuleIds
    .map(id => moduleAppsCatalog[id])
    .filter(Boolean);

  const handleDownload = (filename: string, appName: string) => {
    setDownloadSuccess(`Downloading ${appName}... Check your downloads folder!`);
    
    const link = document.createElement('a');
    link.href = `/downloads/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      setDownloadSuccess('');
    }, 4000);
  };

  const handleDirectLaunch = (modId: string) => {
    if (onLaunchModule) {
      onLaunchModule(modId);
    } else {
      window.location.href = `/?module=${modId}`;
    }
  };

  const handleCopyShortcutLink = (modId: string) => {
    const directUrl = `${window.location.origin}/?module=${modId}`;
    navigator.clipboard.writeText(directUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  return (
    <div className="pt-28 pb-20 px-4 sm:px-6 max-w-7xl mx-auto space-y-12 bg-white min-h-screen">
      
      {/* Header Banner - Clarvos Dark Bento */}
      <div className="bg-[#1c1b1f] text-white rounded-[36px] p-8 md:p-14 border border-white/10 shadow-2xl relative overflow-hidden space-y-6 text-center max-w-5xl mx-auto">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 bg-[#ffd64e]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#ffd64e] text-black text-xs font-black uppercase tracking-wider shadow-sm">
          <ShieldCheck className="w-4 h-4 text-black" />
          <span>Unified Enterprise Architecture • Zero Device Bloat</span>
        </div>

        <h1 className="text-3xl md:text-5xl font-black text-white tracking-tight leading-tight max-w-3xl mx-auto">
          One Unified Clinical Platform.
          <span className="block text-[#ffd64e] mt-1 font-serif italic text-2xl md:text-4xl font-normal">
            31 Specialized Workstations. Zero Device Clutter.
          </span>
        </h1>

        <p className="text-slate-300 text-sm md:text-base leading-relaxed max-w-2xl mx-auto font-normal">
          Instead of the operational nightmare of installing, updating, and managing 93 separate apps across school tablets, AbilityOS delivers a certified, single-install core architecture. Run all 31 clinical tools with 1-tap condition profiles and instant home screen shortcuts.
        </p>

        {downloadSuccess && (
          <div className="p-4 bg-[#ffd64e]/20 border border-[#ffd64e] text-[#ffd64e] text-xs font-black rounded-full shadow-lg flex items-center justify-center gap-2 max-w-md mx-auto animate-bounce">
            <CheckCircle2 className="w-4 h-4 text-[#ffd64e]" />
            <span>{downloadSuccess}</span>
          </div>
        )}
      </div>

      {/* MASTER FULL SUITE INSTALLERS (ONE CERTIFIED INSTALLER PER OS) */}
      <div className="space-y-6 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1c1b1f]/10 pb-4">
          <div>
            <span className="text-[10px] font-black tracking-wider uppercase px-2.5 py-1 rounded-full bg-[#ffd64e] text-black shadow-xs">
              Certified Master Binaries
            </span>
            <h2 className="text-2xl font-black text-[#1c1b1f] mt-1.5 flex items-center gap-2">
              <Layers className="w-6 h-6 text-[#ffd64e]" />
              Install AbilityOS Once. Run All 31 Workstations.
            </h2>
            <p className="text-xs text-slate-600 mt-0.5">
              Available for Android tablets, Windows clinical laptops, macOS workstations, and instant zero-install Web PWA.
            </p>
          </div>
          <span className="text-xs font-black text-[#1c1b1f] bg-[#f9f6f0] border border-black/10 px-3.5 py-1.5 rounded-full self-start sm:self-auto">
            Single Fleet MDM Rollout
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {fullApps.map((app, idx) => {
            const Icon = app.icon;
            return (
              <div 
                key={idx} 
                className="bg-[#f9f6f0] rounded-[32px] border border-[#1c1b1f]/10 p-7 shadow-sm hover:shadow-xl hover:border-[#ffd64e] transition-all duration-300 flex flex-col justify-between space-y-6 group relative overflow-hidden"
              >
                <div className="space-y-5">
                  <div className="flex items-center justify-between">
                    <div className="w-14 h-14 bg-[#1c1b1f] text-[#ffd64e] rounded-2xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                      <Icon className="w-7 h-7" />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-white text-slate-800 border border-slate-200 shadow-xs">
                      {app.highlight}
                    </span>
                  </div>

                  <div>
                    <h3 className="text-lg font-black text-[#1c1b1f] leading-snug">{app.title}</h3>
                    <p className="text-xs text-slate-600 mt-1 font-medium leading-relaxed">{app.platform}</p>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-700 bg-white p-3.5 rounded-2xl font-mono border border-[#1c1b1f]/5">
                    <span>{app.version}</span>
                    <span className="font-black text-[#1c1b1f]">{app.size}</span>
                  </div>
                </div>

                <button
                  onClick={() => handleDownload(app.filename, app.title)}
                  className="w-full py-3.5 bg-[#ffd64e] hover:bg-amber-400 text-black font-black rounded-full text-xs shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Download className="w-4 h-4 text-black" />
                  <span>Download Master Suite Package</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* WHY ONE UNIFIED APP? ZERO-BLOAT ENTERPRISE ADVANTAGE BANNER */}
      <div className="bg-[#f9f6f0] border border-[#1c1b1f]/10 rounded-[32px] p-6 sm:p-8 max-w-6xl mx-auto shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-[#ffd64e]" />
          <h3 className="text-base font-black text-[#1c1b1f] uppercase tracking-wider text-xs">
            Why Unified Architecture Wins Over 93 Disparate Apps
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-1.5">
            <div className="flex items-center gap-2 text-xs font-black text-[#1c1b1f]">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>Guided Access Kiosk</span>
            </div>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              Lock student tablets to their specific RPwD condition profile with a PIN. Students never get lost switching apps.
            </p>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-1.5">
            <div className="flex items-center gap-2 text-xs font-black text-[#1c1b1f]">
              <Radio className="w-4 h-4 text-sky-600" />
              <span>Unified Offline Storage</span>
            </div>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              Dexie.js IndexedDB shares speech data, IEP milestones, and SOAP notes in one encrypted vault with zero data siloing.
            </p>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-1.5">
            <div className="flex items-center gap-2 text-xs font-black text-[#1c1b1f]">
              <Plus className="w-4 h-4 text-amber-600" />
              <span>1-Tap Home Shortcuts</span>
            </div>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              Users can still pin individual tools (AAC, Dr. Speech, Audiometer) directly to their home screen as micro-apps.
            </p>
          </div>

          <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-1.5">
            <div className="flex items-center gap-2 text-xs font-black text-[#1c1b1f]">
              <Zap className="w-4 h-4 text-purple-600" />
              <span>1-Click Fleet Updates</span>
            </div>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              Deploy updates once via web or MDM. Every clinical tool updates in 3 seconds without downloading 90+ separate patches.
            </p>
          </div>
        </div>
      </div>

      {/* DISABILITY CONDITION SELECTOR (RPwD Act 2016 & DSM-5) */}
      <div className="space-y-6 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1c1b1f]/10 pb-4">
          <div>
            <span className="text-[10px] font-black tracking-wider uppercase px-2.5 py-1 rounded-full bg-[#1c1b1f] text-[#ffd64e]">
              Clinical Prescription Taxonomy
            </span>
            <h2 className="text-2xl font-black text-[#1c1b1f] mt-1 flex items-center gap-2">
              <Brain className="w-6 h-6 text-[#ffd64e]" />
              Prescribed Workstations Sorted by Disability
            </h2>
            <p className="text-xs text-slate-600 mt-0.5">
              Launch directly in-browser or pin individual micro-app shortcuts to your home screen.
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-[#f9f6f0] p-1 rounded-full border border-black/10">
              <button
                onClick={() => setViewMode('selected')}
                className={`px-3.5 py-1.5 rounded-full text-xs font-black transition-all cursor-pointer ${
                  viewMode === 'selected'
                    ? 'bg-[#1c1b1f] text-[#ffd64e] shadow-sm'
                    : 'text-slate-600 hover:text-black'
                }`}
              >
                By Condition Tab
              </button>
              <button
                onClick={() => setViewMode('all-disabilities')}
                className={`px-3.5 py-1.5 rounded-full text-xs font-black transition-all cursor-pointer ${
                  viewMode === 'all-disabilities'
                    ? 'bg-[#1c1b1f] text-[#ffd64e] shadow-sm'
                    : 'text-slate-600 hover:text-black'
                }`}
              >
                All 8 Disabilities Directory
              </button>
            </div>
            <span className="text-xs font-black bg-[#ffd64e] text-black px-3.5 py-1.5 rounded-full shadow-xs self-start sm:self-auto">
              31 Modules Cataloged
            </span>
          </div>
        </div>

        {/* 8 Condition Pill Selector in Balanced Responsive Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
          {DISABILITY_CLUSTERS.map((cluster) => {
            const Icon = cluster.icon;
            const isSelected = selectedDisabilityId === cluster.id;
            return (
              <button
                key={cluster.id}
                onClick={() => {
                  setSelectedDisabilityId(cluster.id);
                  if (viewMode === 'all-disabilities') setViewMode('selected');
                }}
                className={`p-3 rounded-full text-xs font-black transition-all flex items-center justify-center gap-2 border cursor-pointer select-none ${
                  isSelected && viewMode === 'selected'
                    ? 'bg-[#1c1b1f] text-[#ffd64e] border-[#1c1b1f] shadow-md ring-2 ring-[#ffd64e]/40 scale-[1.01]'
                    : 'bg-[#f9f6f0] text-slate-700 hover:bg-white border-[#1c1b1f]/10'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{cluster.shortName}</span>
              </button>
            );
          })}
        </div>

        {/* Active Disability Context Bento Card (Shown in selected mode) */}
        {viewMode === 'selected' && (
          <div className="p-6 sm:p-8 bg-[#f9f6f0] rounded-[32px] border border-[#1c1b1f]/10 flex flex-col lg:flex-row lg:items-center justify-between gap-6 shadow-sm">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-2xl bg-[#1c1b1f] text-[#ffd64e] flex items-center justify-center shrink-0 shadow-md">
                <ClusterIcon className="w-6 h-6" />
              </div>
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-black text-[#1c1b1f]">{currentCluster.name}</h3>
                  <span className="px-3 py-0.5 bg-[#ffd64e] text-black text-[10px] font-black rounded-full shadow-xs">
                    {currentCluster.rpwdAlignment}
                  </span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed max-w-2xl">
                  {currentCluster.description}
                </p>
              </div>
            </div>

            {/* Accommodations & Goals */}
            <div className="shrink-0 space-y-2 lg:border-l lg:border-slate-200 lg:pl-6">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Clinical Accommodations:</div>
              <div className="flex flex-wrap gap-1.5 max-w-sm">
                {currentCluster.accommodations.map((acc, aidx) => (
                  <span key={aidx} className="px-2.5 py-1 bg-white text-slate-800 border border-slate-200 rounded-full text-[10px] font-bold shadow-xs">
                    ✓ {acc}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* SEGMENTED OS PLATFORM FILTER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#1c1b1f]/10 pb-4 max-w-6xl mx-auto">
        <div>
          <h3 className="text-lg font-black text-[#1c1b1f] flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#ffd64e]" />
            Target Operating System Filter
          </h3>
          <p className="text-xs text-slate-600 mt-0.5">Filter binary packages by Android APK, Windows Desktop EXE, or macOS DMG.</p>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 bg-[#f9f6f0] p-1.5 rounded-full border border-[#1c1b1f]/10 self-start sm:self-auto">
          {[
            { id: 'all', label: 'All OS Platforms', icon: Layers },
            { id: 'android', label: 'Android (.APK)', icon: Smartphone },
            { id: 'windows', label: 'Windows (.EXE)', icon: Monitor },
            { id: 'macos', label: 'macOS (.DMG)', icon: Apple }
          ].map((tab) => {
            const Icon = tab.icon;
            const isSelected = platformFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setPlatformFilter(tab.id as any)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-black transition-all flex items-center gap-1.5 cursor-pointer ${
                  isSelected
                    ? 'bg-[#1c1b1f] text-[#ffd64e] shadow-sm'
                    : 'text-slate-700 hover:text-black'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* VIEW MODE 1: SELECTED CONDITION APPS */}
      {viewMode === 'selected' && (
        <div className="space-y-8 max-w-6xl mx-auto">
          {/* SECTION 1: PRIMARY INTERVENTION APPS FOR THIS DISABILITY */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#ffd64e]" />
              <h3 className="text-base font-black text-[#1c1b1f] uppercase tracking-wider text-xs">
                Primary Therapeutic Workstations ({primaryApps.length} Workstations for {currentCluster.shortName})
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {primaryApps.map((m) => (
                <div 
                  key={m.id} 
                  className="bg-white rounded-[32px] border-2 border-[#ffd64e] p-6 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between space-y-4 group relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-[#ffd64e]/15 rounded-bl-[40px] pointer-events-none" />

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black font-mono px-3 py-1 bg-[#1c1b1f] text-[#ffd64e] rounded-full">
                        PRIMARY WORKSPACE
                      </span>
                      <span className="text-[10px] font-black text-amber-900 bg-amber-200/60 px-2.5 py-0.5 rounded-full">
                        {currentCluster.shortName}
                      </span>
                    </div>

                    <h4 className="text-base font-black text-[#1c1b1f] leading-snug group-hover:text-amber-600 transition-colors">
                      {m.name}
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed font-normal">
                      {m.desc}
                    </p>
                  </div>

                  {/* ACTION CONTROLS */}
                  <div className="pt-3 border-t border-slate-100 space-y-2.5">
                    {/* Direct Launch & Shortcut Buttons */}
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleDirectLaunch(m.id)}
                        className="py-2.5 px-3 bg-[#1c1b1f] hover:bg-black text-[#ffd64e] text-xs font-black rounded-full flex items-center justify-center gap-1.5 shadow-sm hover:shadow transition-all cursor-pointer"
                      >
                        <Zap className="w-3.5 h-3.5 text-[#ffd64e]" />
                        <span>Launch Direct</span>
                      </button>

                      <button
                        onClick={() => setShortcutModalModule(m)}
                        className="py-2.5 px-3 bg-[#f9f6f0] hover:bg-[#ffd64e] text-[#1c1b1f] text-xs font-black rounded-full border border-black/10 flex items-center justify-center gap-1.5 shadow-xs transition-all cursor-pointer"
                      >
                        <Plus className="w-3.5 h-3.5 text-black" />
                        <span>Pin Shortcut</span>
                      </button>
                    </div>

                    {/* Legacy Binary Downloads */}
                    <div className="pt-1">
                      <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center justify-between">
                        <span>Legacy Standalone Packages:</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1">
                        <button
                          onClick={() => handleDownload(`AbilityOS_${m.filePrefix}_Android.apk`, `${m.name} Android APK`)}
                          className={`py-1.5 rounded-lg text-[10px] font-bold transition-all flex items-center justify-center gap-1 cursor-pointer ${
                            platformFilter === 'android'
                              ? 'bg-[#1c1b1f] text-[#ffd64e]'
                              : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          <Smartphone className="w-2.5 h-2.5" />
                          <span>.APK</span>
                        </button>

                        <button
                          onClick={() => handleDownload(`AbilityOS_${m.filePrefix}_Windows_Setup.exe`, `${m.name} Windows EXE`)}
                          className={`py-1.5 rounded-lg text-[10px] font-bold transition-all flex items-center justify-center gap-1 cursor-pointer ${
                            platformFilter === 'windows'
                              ? 'bg-[#1c1b1f] text-[#ffd64e]'
                              : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          <Monitor className="w-2.5 h-2.5" />
                          <span>.EXE</span>
                        </button>

                        <button
                          onClick={() => handleDownload(`AbilityOS_${m.filePrefix}_macOS.dmg`, `${m.name} macOS DMG`)}
                          className={`py-1.5 rounded-lg text-[10px] font-bold transition-all flex items-center justify-center gap-1 cursor-pointer ${
                            platformFilter === 'macos'
                              ? 'bg-[#1c1b1f] text-[#ffd64e]'
                              : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          <Apple className="w-2.5 h-2.5" />
                          <span>.DMG</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SECTION 2: CROSS-DISCIPLINARY & SUPPORT APPS FOR THIS DISABILITY */}
          {secondaryApps.length > 0 && (
            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-slate-400" />
                <h3 className="text-base font-black text-[#1c1b1f] uppercase tracking-wider text-xs">
                  Supportive & Cross-Discipline Tools ({secondaryApps.length} Tools)
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {secondaryApps.map((m) => (
                  <div 
                    key={m.id} 
                    className="bg-[#f9f6f0] rounded-[32px] border border-[#1c1b1f]/10 p-6 shadow-sm hover:shadow-md hover:border-[#ffd64e] transition-all duration-300 flex flex-col justify-between space-y-4 group"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black font-mono px-3 py-1 bg-white text-slate-700 rounded-full border border-slate-200">
                          SUPPORT TOOL
                        </span>
                        <span className="text-[10px] font-bold text-slate-500">
                          Adjunct Workspace
                        </span>
                      </div>

                      <h4 className="text-base font-black text-[#1c1b1f] leading-snug group-hover:text-amber-600 transition-colors">
                        {m.name}
                      </h4>
                      <p className="text-xs text-slate-600 leading-relaxed font-normal">
                        {m.desc}
                      </p>
                    </div>

                    <div className="pt-3 border-t border-slate-200/60 space-y-2.5">
                      {/* Direct Launch & Shortcut Buttons */}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleDirectLaunch(m.id)}
                          className="py-2.5 px-3 bg-[#1c1b1f] hover:bg-black text-[#ffd64e] text-xs font-black rounded-full flex items-center justify-center gap-1.5 shadow-sm hover:shadow transition-all cursor-pointer"
                        >
                          <Zap className="w-3.5 h-3.5 text-[#ffd64e]" />
                          <span>Launch Direct</span>
                        </button>

                        <button
                          onClick={() => setShortcutModalModule(m)}
                          className="py-2.5 px-3 bg-white hover:bg-[#ffd64e] text-[#1c1b1f] text-xs font-black rounded-full border border-black/10 flex items-center justify-center gap-1.5 shadow-xs transition-all cursor-pointer"
                        >
                          <Plus className="w-3.5 h-3.5 text-black" />
                          <span>Pin Shortcut</span>
                        </button>
                      </div>

                      {/* Legacy Binaries */}
                      <div className="pt-1">
                        <div className="grid grid-cols-3 gap-1">
                          <button
                            onClick={() => handleDownload(`AbilityOS_${m.filePrefix}_Android.apk`, `${m.name} Android APK`)}
                            className="py-1.5 rounded-lg text-[10px] font-bold bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 flex items-center justify-center gap-1 cursor-pointer"
                          >
                            <Smartphone className="w-2.5 h-2.5" />
                            <span>.APK</span>
                          </button>

                          <button
                            onClick={() => handleDownload(`AbilityOS_${m.filePrefix}_Windows_Setup.exe`, `${m.name} Windows EXE`)}
                            className="py-1.5 rounded-lg text-[10px] font-bold bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 flex items-center justify-center gap-1 cursor-pointer"
                          >
                            <Monitor className="w-2.5 h-2.5" />
                            <span>.EXE</span>
                          </button>

                          <button
                            onClick={() => handleDownload(`AbilityOS_${m.filePrefix}_macOS.dmg`, `${m.name} macOS DMG`)}
                            className="py-1.5 rounded-lg text-[10px] font-bold bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 flex items-center justify-center gap-1 cursor-pointer"
                          >
                            <Apple className="w-2.5 h-2.5" />
                            <span>.DMG</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* VIEW MODE 2: ALL 8 DISABILITIES DIRECTORY */}
      {viewMode === 'all-disabilities' && (
        <div className="space-y-12 max-w-6xl mx-auto">
          {DISABILITY_CLUSTERS.map((clust) => {
            const CIcon = clust.icon;
            const cPrimary = clust.primaryModuleIds.map(id => moduleAppsCatalog[id]).filter(Boolean);
            const cSecondary = clust.secondaryModuleIds.map(id => moduleAppsCatalog[id]).filter(Boolean);

            return (
              <div key={clust.id} className="space-y-4 border-t border-slate-200 pt-8 first:border-t-0 first:pt-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#1c1b1f] text-[#ffd64e] flex items-center justify-center shadow-xs">
                    <CIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-[#1c1b1f]">{clust.name}</h3>
                    <p className="text-xs text-slate-600">{clust.rpwdAlignment} • {cPrimary.length + cSecondary.length} Workstations</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cPrimary.map((m) => (
                    <div 
                      key={m.id} 
                      className="bg-white rounded-[28px] border-2 border-[#ffd64e] p-5 shadow-xs flex flex-col justify-between space-y-3"
                    >
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-full bg-[#1c1b1f] text-[#ffd64e]">
                            Primary
                          </span>
                          <span className="text-[10px] font-bold text-amber-700">
                            {clust.shortName}
                          </span>
                        </div>
                        <h4 className="text-sm font-black text-[#1c1b1f] leading-snug">{m.name}</h4>
                        <p className="text-[11px] text-slate-600 leading-relaxed">{m.desc}</p>
                      </div>

                      <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-1.5">
                        <button
                          onClick={() => handleDirectLaunch(m.id)}
                          className="flex-1 py-1.5 rounded-full bg-[#1c1b1f] text-[#ffd64e] text-[10px] font-black flex items-center justify-center gap-1 cursor-pointer"
                        >
                          <Zap className="w-3 h-3" />
                          <span>Launch</span>
                        </button>
                        <button
                          onClick={() => setShortcutModalModule(m)}
                          className="flex-1 py-1.5 rounded-full bg-[#f9f6f0] hover:bg-[#ffd64e] text-slate-800 text-[10px] font-bold border border-black/10 flex items-center justify-center gap-1 cursor-pointer"
                        >
                          <Plus className="w-3 h-3" />
                          <span>Pin</span>
                        </button>
                      </div>
                    </div>
                  ))}

                  {cSecondary.map((m) => (
                    <div 
                      key={m.id} 
                      className="bg-white rounded-[28px] border border-black/10 p-5 shadow-xs flex flex-col justify-between space-y-3"
                    >
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                            Support Tool
                          </span>
                          <span className="text-[10px] font-bold text-slate-500">
                            {clust.shortName}
                          </span>
                        </div>
                        <h4 className="text-sm font-black text-[#1c1b1f] leading-snug">{m.name}</h4>
                        <p className="text-[11px] text-slate-600 leading-relaxed">{m.desc}</p>
                      </div>

                      <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-1.5">
                        <button
                          onClick={() => handleDirectLaunch(m.id)}
                          className="flex-1 py-1.5 rounded-full bg-[#1c1b1f] text-[#ffd64e] text-[10px] font-black flex items-center justify-center gap-1 cursor-pointer"
                        >
                          <Zap className="w-3 h-3" />
                          <span>Launch</span>
                        </button>
                        <button
                          onClick={() => setShortcutModalModule(m)}
                          className="flex-1 py-1.5 rounded-full bg-[#f9f6f0] hover:bg-[#ffd64e] text-slate-800 text-[10px] font-bold border border-black/10 flex items-center justify-center gap-1 cursor-pointer"
                        >
                          <Plus className="w-3 h-3" />
                          <span>Pin</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL: PIN TO HOME SCREEN & PWA MICRO-APP SHORTCUT */}
      {shortcutModalModule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-[#1c1b1f] text-white rounded-[32px] border border-white/10 p-6 sm:p-8 max-w-lg w-full shadow-2xl space-y-6 relative">
            <button
              onClick={() => setShortcutModalModule(null)}
              className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer transition"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="space-y-2">
              <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-[#ffd64e] text-black">
                1-Tap Micro-App Shortcut
              </span>
              <h3 className="text-xl font-black text-white">
                Pin "{shortcutModalModule.name}" to Home Screen
              </h3>
              <p className="text-xs text-slate-300">
                You do not need to install a separate bloated app! Create a dedicated 1-tap desktop or home screen launcher that opens directly into this clinical tool.
              </p>
            </div>

            {/* Platform Instructions */}
            <div className="space-y-3 bg-white/5 p-4 rounded-2xl border border-white/10 text-xs">
              <div className="flex items-start gap-2.5">
                <Smartphone className="w-4 h-4 text-[#ffd64e] shrink-0 mt-0.5" />
                <div>
                  <div className="font-black text-white">Android & Chrome:</div>
                  <div className="text-slate-300 text-[11px]">
                    Tap Chrome menu <span className="font-mono bg-white/10 px-1 py-0.5 rounded">⋮</span> ➔ Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Apple className="w-4 h-4 text-[#ffd64e] shrink-0 mt-0.5" />
                <div>
                  <div className="font-black text-white">iPad & iPhone (Safari):</div>
                  <div className="text-slate-300 text-[11px]">
                    Tap the Share icon <Share2 className="w-3 h-3 inline" /> ➔ Scroll down ➔ Tap <strong>"Add to Home Screen"</strong>.
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Monitor className="w-4 h-4 text-[#ffd64e] shrink-0 mt-0.5" />
                <div>
                  <div className="font-black text-white">Windows & Mac (Chrome / Edge):</div>
                  <div className="text-slate-300 text-[11px]">
                    Click the Install icon in the browser address bar ➔ Click <strong>"Install AbilityOS"</strong>.
                  </div>
                </div>
              </div>
            </div>

            {/* Direct Deep-Link Action */}
            <div className="space-y-2">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Direct Deep-Link URL:
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/?module=${shortcutModalModule.id}`}
                  className="w-full px-3 py-2 rounded-xl bg-white/10 border border-white/15 text-xs text-slate-200 font-mono focus:outline-hidden"
                />
                <button
                  onClick={() => handleCopyShortcutLink(shortcutModalModule.id)}
                  className="px-4 py-2 bg-[#ffd64e] hover:bg-amber-400 text-black text-xs font-black rounded-xl shrink-0 flex items-center gap-1 cursor-pointer transition"
                >
                  {copiedLink ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedLink ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
            </div>

            <div className="pt-2 flex items-center justify-end gap-2 border-t border-white/10">
              <button
                onClick={() => {
                  const modId = shortcutModalModule.id;
                  setShortcutModalModule(null);
                  handleDirectLaunch(modId);
                }}
                className="w-full py-3 bg-[#ffd64e] hover:bg-amber-400 text-black font-black text-xs rounded-full shadow-md flex items-center justify-center gap-2 cursor-pointer transition"
              >
                <Zap className="w-4 h-4 text-black" />
                <span>Launch "{shortcutModalModule.name}" Now</span>
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
